import { useEffect, useRef, useState } from "react";
import {
  AreaSeries, CandlestickSeries, HistogramSeries, LineSeries, createChart,
} from "lightweight-charts";
import { get } from "../api";
import { toHeikinAshi } from "./heikin";
import { INDICATORS, computeIndicator } from "./indicators"; // Task 4

const TF_LIST = ["M1", "M5", "M15", "M30", "H1", "H4", "D1", "W1", "MN1"];
const TYPES = [
  ["candles", "Candles"], ["line", "Line"], ["area", "Area"], ["heikin", "Heikin-Ashi"],
];
const FALLBACK_SYMBOLS = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD", "ETHUSD"];
const POPULAR_SYMBOLS = [
  "EUR/USD", "GBP/USD", "USD/JPY", "USD/CHF", "AUD/USD", "NZD/USD", "USD/CAD", "EUR/GBP",
  "XAU/USD", "XAG/USD",
  "BTC/USD", "ETH/USD",
  "BRENT.CMD/USD", "LIGHT.CMD/USD",
  "USA500.IDX/USD", "USATECH.IDX/USD", "USA30.IDX/USD", "DEU.IDX/EUR",
];
const PRICE_OPTS = {
  upColor: "#059669", downColor: "#dc2626",
  wickUpColor: "#047857", wickDownColor: "#b91c1c", borderVisible: false,
};

export default function FullChart({ markets = [], defaultSymbol = "EURUSD" }) {
  const holder = useRef(null), boxRef = useRef(null);
  const symbolPickerRef = useRef(null), indPickerRef = useRef(null), pmPickerRef = useRef(null);
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [tf, setTf] = useState("H1");
  const [type, setType] = useState("candles");
  const [activeIndicators, setActiveIndicators] = useState([]);
  const [indOpen, setIndOpen] = useState(false);
  const [catalog, setCatalog] = useState(FALLBACK_SYMBOLS);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [overlayMarket, setOverlayMarket] = useState(null);
  const [pmOpen, setPmOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [overlayPoints, setOverlayPoints] = useState([]);
  const [overlayLoaded, setOverlayLoaded] = useState(true);

  useEffect(() => {
    // Live shape (verified 2026-08-31 against https://capdeskpro.com/api/market-data/symbol-catalog):
    // { status: "ok", count: 1503, symbols: [ { symbol: "BUND.TR/EUR", dukascopy_symbol: "BUNDTREUR",
    //   display_symbol: "BUND.TR/EUR", category: "Bonds CFD", ... }, ... ] }
    // The normalization below takes r.symbols, maps objects to .symbol, and also tolerates
    // a bare string[] payload so either catalog shape works.
    get("/api/market-data/symbol-catalog")
      .then((r) => {
        const list = (Array.isArray(r) ? r : r.symbols || r.data || [])
          .map((x) => (typeof x === "string" ? x : x.symbol)).filter(Boolean);
        if (list.length) setCatalog(list);
      })
      .catch(() => {});
  }, []);

  // Close any open picker on outside click or Escape. The listeners only exist
  // while at least one picker is open, and are removed on close/unmount.
  useEffect(() => {
    if (!pickerOpen && !indOpen && !pmOpen) return undefined;
    const closeAll = () => { setPickerOpen(false); setIndOpen(false); setPmOpen(false); };
    const onMouseDown = (e) => {
      const inside = [symbolPickerRef, indPickerRef, pmPickerRef]
        .some((r) => r.current?.contains(e.target));
      if (!inside) closeAll();
    };
    const onKeyDown = (e) => { if (e.key === "Escape") closeAll(); };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pickerOpen, indOpen, pmOpen]);

  // Candle fetch — runs on symbol/timeframe change only. Chart-type and
  // indicator changes re-render from the cached `rows` state, no refetch.
  useEffect(() => {
    let cancel = false;
    setError("");
    get(`/api/market-data/candles?symbol=${encodeURIComponent(symbol)}&timeframe=${tf}&limit=500`)
      .then((payload) => {
        if (cancel) return;
        setRows((Array.isArray(payload) ? payload : payload.candles || payload.data || [])
          .map((r) => ({
            time: Math.floor(new Date(r.start_at || r.time).getTime() / 1000),
            open: +r.open, high: +r.high, low: +r.low, close: +r.close,
            volume: +r.volume || 0,
          }))
          .filter((r) => Number.isFinite(r.time) && Number.isFinite(r.open))
          .sort((a, b) => a.time - b.time)
          // setData() throws on duplicate timestamps; the feed can emit them
          .filter((r, i, arr) => i === 0 || r.time !== arr[i - 1].time));
      })
      .catch((e) => { if (!cancel) setError(e.message); });
    return () => { cancel = true; };
  }, [symbol, tf]);

  // Polymarket overlay history fetch. Aborted via AbortController when the
  // selected market changes or the component unmounts; the cancel flag stays
  // as a second guard so a late reject after abort can't touch state.
  useEffect(() => {
    let cancel = false;
    const controller = new AbortController();
    setOverlayPoints([]);
    if (!overlayMarket) {
      setOverlayLoaded(true);
    } else {
      setOverlayLoaded(false);
      // raw fetch instead of the api helper: ../api request() owns its
      // AbortController internally and can't take an external signal
      fetch(`/frontend-api/prediction-markets/${encodeURIComponent(overlayMarket.id)}/history?interval=1w&fidelity=60`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      })
        .then((r) => (r.ok ? r.json().catch(() => ({})) : {}))
        .then((payload) => {
          if (cancel) return;
          // sort + dedupe: setData() throws on unsorted or duplicate timestamps
          setOverlayPoints((payload.history || [])
            .map((p) => ({ time: Number(p.time), value: Number(p.probability) * 100 }))
            .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.value))
            .sort((a, b) => a.time - b.time)
            .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time));
          setOverlayLoaded(true);
        })
        .catch((e) => {
          if (cancel || e?.name === "AbortError") return;
          setOverlayPoints([]);
          setOverlayLoaded(true);
        });
    }
    return () => { cancel = true; controller.abort(); };
  }, [overlayMarket?.id]);

  useEffect(() => {
    if (!holder.current) return undefined;
    const anyLower = activeIndicators.some(
      (k) => INDICATORS.find((x) => x.key === k)?.pane === "lower");
    const chart = createChart(holder.current, {
      layout: {
        background: { color: "#fff" }, textColor: "#1f2937",
        fontFamily: "Inter,Arial,sans-serif", attributionLogo: false,
      },
      grid: { vertLines: { color: "#f4f5f7" }, horzLines: { color: "#f4f5f7" } },
      rightPriceScale: { borderColor: "#e3e5e8" },
      timeScale: { borderColor: "#e3e5e8", timeVisible: true },
    });

    const data = type === "heikin" ? toHeikinAshi(rows) : rows;
    if (rows.length) {
      if (type === "candles" || type === "heikin") {
        chart.addSeries(CandlestickSeries, PRICE_OPTS).setData(data);
      } else if (type === "area") {
        chart.addSeries(AreaSeries, { lineColor: "#3867e8", topColor: "rgba(56,103,232,.25)", bottomColor: "rgba(56,103,232,.02)" })
          .setData(data.map((r) => ({ time: r.time, value: r.close })));
      } else {
        chart.addSeries(LineSeries, { color: "#3867e8", lineWidth: 2 })
          .setData(data.map((r) => ({ time: r.time, value: r.close })));
      }
      const vol = chart.addSeries(HistogramSeries, {
        priceScaleId: "volume",
        priceFormat: { type: "volume" },
      });
      vol.setData(rows.map((r) => ({
        time: r.time, value: r.volume,
        color: r.close >= r.open ? "rgba(5,150,105,.35)" : "rgba(220,38,38,.35)",
      })));
      // when a lower-pane indicator (RSI/MACD) is active it owns the bottom
      // 20%; keep the volume underlay above it, inside the price band
      chart.priceScale("volume").applyOptions({
        scaleMargins: anyLower ? { top: 0.6, bottom: 0.25 } : { top: 0.85, bottom: 0 },
        borderVisible: false,
      });
    }

    chart.priceScale("right").applyOptions({
      scaleMargins: anyLower ? { top: 0.05, bottom: 0.25 } : { top: 0.2, bottom: 0.1 },
    });
    for (const key of activeIndicators) {
      const meta = INDICATORS.find((x) => x.key === key);
      const descriptors = computeIndicator(key, rows);
      if (!descriptors.length) continue;
      for (const d of descriptors) {
        const Series = d.kind === "histogram" ? HistogramSeries : LineSeries;
        const opts = d.kind === "histogram"
          ? { priceScaleId: d.priceScaleId }
          : { color: d.color, lineWidth: 1, priceScaleId: d.priceScaleId || "right", crosshairMarkerVisible: false };
        const s = chart.addSeries(Series, opts);
        s.setData(d.data);
      }
      // priceScale(key) only exists after a series is attached to it (v5), so
      // the band margins go on after the addSeries calls above.
      if (meta.pane === "lower") {
        chart.priceScale(key).applyOptions({ scaleMargins: { top: 0.8, bottom: 0 }, borderVisible: false });
      }
    }
    // Task 5: overlay probability line on "probability" price scale
    if (overlayMarket && overlayPoints.length) {
      const line = chart.addSeries(LineSeries, {
        color: "#3867e8", lineWidth: 2, priceScaleId: "probability",
        priceFormat: { type: "custom", formatter: (v) => `${v.toFixed(0)}%` },
      });
      line.setData(overlayPoints);
      chart.priceScale("probability").applyOptions({ scaleMargins: { top: 0.12, bottom: 0.16 }, borderVisible: false });
    }

    chart.timeScale().fitContent();
    const observer = new ResizeObserver((e) =>
      chart?.applyOptions({ width: e[0].contentRect.width, height: e[0].contentRect.height }));
    observer.observe(holder.current);
    return () => { observer.disconnect(); chart.remove(); };
  }, [rows, type, activeIndicators, overlayMarket, overlayPoints]);

  useEffect(() => {
    if (overlayMarket && !overlayLoaded) {
      setStatus(`${rows.length} candles · loading probability…`);
    } else if (overlayMarket && overlayPoints.length) {
      setStatus(`${rows.length} candles · ${overlayPoints.length} Polymarket points`);
    } else if (overlayMarket) {
      setStatus(`${rows.length} candles · probability history unavailable`);
    } else {
      setStatus(`${rows.length} candles`);
    }
  }, [rows, overlayPoints, overlayMarket, overlayLoaded]);

  const q = query.toUpperCase().replaceAll("/", "");
  // Empty query: lead with a curated popular set resolved against the loaded
  // catalog (slash or plain forms) instead of the first 50 alphabetical
  // entries, which are mostly bonds/commodities.
  const popular = POPULAR_SYMBOLS.map((want) =>
    catalog.find((s) => s.replaceAll("/", "").toUpperCase() === want.replaceAll("/", ""))
  ).filter(Boolean);
  const shown = q
    ? catalog.filter((s) => s.replaceAll("/", "").includes(q)).slice(0, 50)
    : popular;

  return (
    <div className="fullchart" ref={boxRef}>
      <div className="fc-toolbar">
        <div className="fc-picker" ref={symbolPickerRef}>
          <button className="fc-symbol" onClick={() => setPickerOpen((v) => !v)}>{symbol} ▾</button>
          {pickerOpen && (
            <div className="fc-picker-list">
              <input autoFocus placeholder="Search symbols" value={query}
                onChange={(e) => setQuery(e.target.value)} />
              {shown.map((s) => (
                <button key={s} onClick={() => { setSymbol(s); setPickerOpen(false); setQuery(""); }}>{s}</button>
              ))}
              {!q && (
                <div className="fc-picker-note">
                  — {catalog.length.toLocaleString()} symbols · type to search —
                </div>
              )}
            </div>
          )}
        </div>
        <div className="fc-tf">
          {TF_LIST.map((t) => (
            <button key={t} className={t === tf ? "active" : ""} onClick={() => setTf(t)}>{t}</button>
          ))}
        </div>
        <div className="fc-types">
          {TYPES.map(([k, label]) => (
            <button key={k} className={k === type ? "active" : ""} onClick={() => setType(k)}>{label}</button>
          ))}
        </div>
        <div className="fc-picker" ref={indPickerRef}>
          <button onClick={() => setIndOpen((v) => !v)}>Indicators ({activeIndicators.length})</button>
          {indOpen && (
            <div className="fc-picker-list">
              {INDICATORS.map((ind) => (
                <button key={ind.key}
                  onClick={() => setActiveIndicators((cur) =>
                    cur.includes(ind.key) ? cur.filter((k) => k !== ind.key) : [...cur, ind.key])}>
                  {activeIndicators.includes(ind.key) ? "✓ " : ""}{ind.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="fc-picker" ref={pmPickerRef}>
          <button onClick={() => setPmOpen((v) => !v)}>
            Polymarket{overlayMarket
              ? `: ${overlayMarket.question.length > 28 ? overlayMarket.question.slice(0, 28) + "…" : overlayMarket.question}`
              : ""}
          </button>
          {pmOpen && (
            <div className="fc-picker-list">
              <button onClick={() => { setOverlayMarket(null); setPmOpen(false); }}>Off</button>
              {markets.map((m) => (
                <button key={m.id} onClick={() => { setOverlayMarket(m); setPmOpen(false); }}>
                  {m.question}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => {
          if (document.fullscreenElement) document.exitFullscreen();
          else boxRef.current?.requestFullscreen?.();
        }}>⛶ Fullscreen</button>
      </div>
      {error
        ? <div className="fc-error">{error}</div>
        : <div className="fc-canvas" ref={holder} />}
      <div className="fc-footer">{status}</div>
    </div>
  );
}
